import { motion } from 'framer-motion';
import { CalendarDays, Info } from 'lucide-react';

interface HeatmapRow {
  projectId: string;
  projectName: string;
  values: number[];
}

interface Props {
  dates: string[];
  rows: HeatmapRow[];
  onRowClick?: (projectId: string) => void;
  title?: string;
  subtitle?: string;
}

const cellColor = (v: number): string => {
  if (v === 2) return 'bg-emerald-500';
  if (v === 1) return 'bg-amber-400';
  return 'bg-slate-200 dark:bg-slate-700';
};

const cellTitle = (v: number, date: string, project: string): string => {
  const status = v === 2 ? 'Full reporting' : v === 1 ? 'Partial reporting' : 'No submission';
  return `${project} • ${date} • ${status}`;
};

export default function ComplianceHeatmap({
  dates,
  rows,
  onRowClick,
  title = 'Daily Reporting Compliance',
  subtitle = 'Last 30 days · click row to drill into project'
}: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10">
            <CalendarDays className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-slate-300 dark:bg-slate-600" /> None</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-400" /> Partial</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> Full</span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500">
          <Info className="h-6 w-6" />
          <p className="text-sm">No reporting data in this window.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[180px_1fr] gap-3">
              <div />
              <div className="grid" style={{ gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))` }}>
                {dates.map((d, i) => (
                  <div
                    key={d}
                    className={`text-center text-[8px] font-semibold uppercase tracking-wide ${
                      i % 7 === 0 ? 'text-slate-500 dark:text-slate-400' : 'text-transparent'
                    }`}
                  >
                    {d.slice(5)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-1.5 space-y-1">
              {rows.map(row => (
                <button
                  key={row.projectId}
                  type="button"
                  onClick={() => onRowClick?.(row.projectId)}
                  className="grid w-full grid-cols-[180px_1fr] items-center gap-3 rounded-lg px-1 py-1 text-left transition hover:bg-slate-50 dark:hover:bg-slate-700/40"
                >
                  <div className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                    {row.projectName}
                  </div>
                  <div className="grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))` }}>
                    {row.values.map((v, i) => (
                      <div
                        key={i}
                        className={`h-4 rounded-sm transition hover:scale-110 ${cellColor(v)}`}
                        title={cellTitle(v, dates[i] ?? '', row.projectName)}
                      />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
